import Minze, { EventListeners, MinzeElement, Reactive, Attrs } from 'minze'
import { Font } from '../../constants'
import { Locale } from '../../modules/locale'

Locale.getInstance().registerKeySet('fr', 'lvc-contributor-type-field-v2', {
  title: 'Type de contributeur*',
  choose: 'Choisir',
})

Locale.getInstance().registerKeySet('en', 'lvc-contributor-type-field-v2', {
  title: 'Contributor type*',
  choose: 'Choose',
})

// Contributor types with FR/EN labels
// The id (value) is the French text as expected by the database
const CONTRIBUTOR_TYPES = [
  { id: 'Person', name: { fr: 'Personne', en: 'Person' } },
  { id: 'Organization', name: { fr: 'Organisation', en: 'Organization' } },
]

export interface LvcContributorTypeFieldV2 {
  placeholder: string
  open: boolean
  onChange: string
  defaultValue: any
  value: string
  error: string
}

export class LvcContributorTypeFieldV2 extends MinzeElement {
  private contributorTypes: typeof CONTRIBUTOR_TYPES = CONTRIBUTOR_TYPES
  private selectedId: string = ''
  private filter: string = ''
  attrs: Attrs = ['title', 'placeholder', ['default-value', ''], ['on-change', ''], ['error', '']]

  reactive: Reactive = [['value', '']]

  static observedAttributes = ['title', 'on-change', 'default-value', 'error']

  async onBlur(e: Event): Promise<void> {
    this.open = false
    this.rerender()
  }

  onAttributeChange(
    name?: string | undefined,
    oldValue?: string | null | undefined,
    newValue?: string | null | undefined
  ): void | Promise<void> {
    if (name === 'default-value' && oldValue !== newValue) {
      this.updateDefaultValue()
    }
  }

  updateDefaultValue() {
    if (this.defaultValue && this.defaultValue !== true) {
      try {
        const parsed = JSON.parse(decodeURIComponent(this.defaultValue))
        if (Array.isArray(parsed) && parsed.length > 0) {
          this.selectedId = parsed[0].id || ''
        } else if (typeof parsed === 'string') {
          this.selectedId = parsed
        }
      } catch (e) {
        this.selectedId = this.defaultValue
      }
    }
    this.rerender()
  }

  async onReady() {
    if (this.defaultValue && this.defaultValue !== true) {
      this.updateDefaultValue()
    }
    this.rerender()
  }

  async onToggle() {
    this.open = !this.open
    this.rerender()
  }

  onContributorTypeClick(e: Event): void {
    let value = (e.target as HTMLElement).getAttribute('data-id')

    if (!value) {
      return
    }

    this.selectedId = value
    this.serializeValue()
    this.onToggle()
    this.rerender()
  }

  async onFilterChange(e: Event) {
    this.filter = (e.target as HTMLInputElement).value
    this.rerender()
  }

  serializeValue() {
    let selectedContributorType = this.selectedId
    this.value = encodeURIComponent(JSON.stringify(selectedContributorType))
    this.cast('change', selectedContributorType)
  }

  getContributorTypesLabel() {
    if (!this.selectedId) {
      return Locale.getKey('lvc-contributor-type-field-v2.choose')
    }

    const selectedContributorType = this.contributorTypes.find(
      contributorType => contributorType.id === this.selectedId
    )

    if (selectedContributorType) {
      return selectedContributorType.name[Locale.language as 'fr' | 'en']
    }

    return ''
  }

  renderContributorType = (contributorType: (typeof CONTRIBUTOR_TYPES)[0]) => {
    const label = contributorType.name[Locale.language as 'fr' | 'en']
    if (this.filter && label.toLowerCase().indexOf(this.filter.toLowerCase()) === -1) {
      return ''
    }

    let isNotSelected = this.selectedId !== contributorType.id

    return `
      <button
        name="contributorType"
        class="${isNotSelected ? 'not-selected' : 'selected'}"
        data-id="${contributorType.id}">
        ${label}
        ${isNotSelected ? '' : '<lvc-circle-check-select-icon></lvc-circle-check-select-icon>'}
      </button>
    `
  }

  renderContributorTypes = () => {
    let html = ''

    for (let i = 0; i < this.contributorTypes.length; i++) {
      html += this.renderContributorType(this.contributorTypes[i])
    }

    return html
  }

  html = () => {
    let showError = typeof this.error === 'string' && this.error.length > 0
    return `
      <div class="title">${Locale.getKey('lvc-contributor-type-field-v2.title')}</div>
      <div class="input ${this.selectedId ? 'has-value' : 'no-value'} ${showError ? 'error' : ''}">
        <button class="dropbtn" name="toggle">
          ${this.getContributorTypesLabel()}
          <lvc-chevron-down-icon></lvc-chevron-down-icon>
        </button>
        <div class="overlay ${this.open ? 'show' : ''}" name="overlay"></div>
        <div class="dropdown-content ${this.open ? 'show' : ''}">
          <div class="header">
            <span>${Locale.getKey('lvc-contributor-type-field-v2.choose')}</span>
            <lvc-chevron-up-icon></lvc-chevron-up-icon>
          </div>
          <div class="contributorTypes" data-selected="${this.selectedId}">
            ${this.renderContributorTypes()}
          </div>
        </div>
      </div>
      ${showError ? `<lvc-error error="${this.error}"></lvc-error>` : ''}
    `
  }

  css = () => {
    return `
      :host {
        width: 100%;
        min-width: 150px;
      }

      .overlay {
        position: fixed;
        top: 0px;
        left: 0px;
        bottom: 0px;
        right: 0px;
        width: 100%;
        height: 100%;
        z-index: 1;
        display: none;
      }
      .overlay.show {
        display: block;
      }

      .header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        width: 100%;
        height: 48px;
        border: none;
        outline: none;
        padding: 0 16px;
        box-sizing: border-box;
        font-family: ${Font.family};
        font-size: 16px;
        font-style: normal;
        font-weight: ${Font.weight.medium};
        line-height: 150%; /* 21px */
        color: var(--Minuit, #1E007D);
        border-top-left-radius: 24px;
        border-top-right-radius: 24px;
        border-bottom: 1px solid var(--Minuit, #1E007D);
        background: var(--Ciel, #BFF0FF);

      }


      input[name="filter"] {
        width: 100%;
        height: 48px;
        border: none;
        outline: none;
        padding: 0 0;
        box-sizing: border-box;
        font-family: ${Font.family};
        font-size: 16px;
        font-style: normal;
        font-weight: ${Font.weight.medium};
        line-height: 150%; /* 21px */
        color: var(--Minuit, #1E007D);
        background: transparent;
      }

      .title {
        color: var(--Minuit, #1E007D);
        font-family: ${Font.family};
        font-size: 14px;
        font-weight: ${Font.weight.medium};
        line-height: 150%; /* 21px */
        margin-bottom: 3.5px;
      }

      .input {
        font-size: 16px;
        display: flex;
        height: 48px;
        flex-direction: column;
        justify-content: center;
        align-items: flex-start;
        flex-shrink: 0;
        align-self: stretch;
        border: 1px solid var(--Minuit, #1E007D);
        position: relative;
        border-radius: 24px;
      }
      
      .input.has-value {
        border: 1px solid rgba(30, 0, 125, 0.3);
        background: #BFF0FF;
      }
      .input.no-value > .dropbtn {
        color: #5a61bf;
      }

      .input.error {
        background: #FFEBD7;
        border-color: #FF4600;
      }

      .contributorTypes {
        display: flex;
        flex-direction: column;
        align-items: stretch;
        justify-content: stretch;
        overflow: hidden;
        overflow-y: auto;
        max-height: calc(300px - 48px);
        padding: 0 10px;
      }

      .contributorTypes button {
        display: flex;
        justify-content: space-between;
        font-family: ${Font.family};
        font-size: 14px;
        line-height: 32px;
        cursor: pointer;
        background-color: transparent;
        color: var(--Minuit, #1E007D);
        text-align: left;
        padding: 8px 8px;
        border: none;
        height: fit-content;
        flex: 0 0 48px;
        border-bottom: 1px solid rgba(30, 0, 125, 0.30);
      }

      .contributorTypes button:last-child {
        border-bottom: none;
      }

      .contributorTypes button.selected {
      }


      /* style.css */

      /* Dropdown Button */
      .dropbtn {
        color: white;
        padding: 12px 16px;
        font-family: ${Font.family};
        font-size: 16px;
        font-weight: ${Font.weight.medium};
        border: none;
        cursor: pointer;
        flex: 1;
        width: 100%;
        background-color: transparent;
        color: var(--Minuit, #1E007D);
        text-align: left;
        display: flex;
        justify-content: space-between;
        align-items: center;
      }

      /* Dropdown button on hover & focus */
      .dropbtn:hover, .dropbtn:focus {
      }

      /* Dropdown Content */
      .dropdown-content {
        width: calc(100% + 2px);
        margin: -1px;
        display: none;
        position: absolute;
        background-color: #f9f9f9;
        min-width: 160px;
        box-shadow: 0px 8px 16px 0px rgba(0,0,0,0.2);
        z-index: 2;
        top: 0;
        border-radius: 24px; 
        max-height: 300px;
        min-height: 96px;
        border: 1px solid var(--Minuit, #1E007D);
        line-height: 32px;
      }

      .dropdown-content.show {
        display: block;
      }


    `
  }

  eventListeners: EventListeners = [
    ['button[name="toggle"]', 'click', this.onToggle.bind(this)],
    ['button[name="contributorType"]', 'click', this.onContributorTypeClick.bind(this)],
    ['input[name="filter"]', 'keyup', this.onFilterChange.bind(this)],
    [this, 'blur', this.onBlur.bind(this)],
    ['div.overlay', 'click', this.onBlur.bind(this)],
  ]
}
